import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, SlidersHorizontal } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import ProLockedOverlay from "@/components/ProLockedOverlay";
import GuestUpgradePrompt from "@/components/GuestUpgradePrompt";

// Only Male / Female. Both Pro-locked.
const genderOptions = ["Male", "Female"];

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (genders: string[], country: string) => void;
  initialGenders: string[];
  initialCountry: string;
}

const FilterPanel = ({ isOpen, onClose, onApply, initialGenders }: FilterPanelProps) => {
  const { user } = useApp();
  const [selectedGenders, setSelectedGenders] = useState<string[]>(
    initialGenders.filter((g) => g === "Male" || g === "Female"),
  );
  const [showProLocked, setShowProLocked] = useState(false);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);

  const isPro = user?.isPro ?? false;
  const isGuest = user?.isAnonymous ?? false;

  // Re-sync when re-opened
  useEffect(() => {
    if (isOpen) {
      setSelectedGenders(initialGenders.filter((g) => g === "Male" || g === "Female"));
    }
  }, [isOpen, initialGenders]);

  const selectGender = (g: string) => {
    if (isGuest) { setShowGuestPrompt(true); return; }
    if (!isPro) { setShowProLocked(true); return; }
    // Single-select. Tap same to clear (= no filter).
    setSelectedGenders((prev) => (prev.length === 1 && prev[0] === g ? [] : [g]));
  };

  const handleApply = () => {
    // Empty = no filter; backend RPC treats ["All"] as "any gender".
    const genders = selectedGenders.length === 0 ? ["All"] : selectedGenders;
    onApply(genders, "Global");
    onClose();
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 350 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-t-2xl bg-card border-t border-border/20 p-5 max-h-[80vh] overflow-y-auto">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/20 mx-auto mb-4" />
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-primary" />
                  <h3 className="font-display font-bold text-foreground text-sm">Filters</h3>
                  {!isPro && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-accent/20 text-accent font-semibold">PRO</span>}
                </div>
                <button onClick={onClose}><X className="w-4 h-4 text-muted-foreground/50" /></button>
              </div>

              {!isPro && (
                <div className="mb-4 rounded-xl p-3 bg-primary/10 border border-primary/30 flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <p className="text-[11px] text-foreground/90 flex-1">
                    Upgrade to <span className="font-bold text-primary">Pro</span> to unlock filters
                  </p>
                </div>
              )}

              <div className="mb-5">
                <label className="text-xs text-muted-foreground mb-2 block">Match with</label>
                <div className="grid grid-cols-2 gap-2">
                  {genderOptions.map((g) => {
                    const locked = !isPro;
                    const active = selectedGenders.includes(g);
                    return (
                      <motion.button
                        key={g}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => selectGender(g)}
                        className={`px-3 py-3 min-h-[48px] rounded-xl text-sm font-semibold transition-all relative ${
                          active
                            ? "gradient-premium text-primary-foreground"
                            : locked
                              ? "bg-muted/20 text-muted-foreground/50 border border-border/10"
                              : "bg-muted/30 text-foreground border border-border/15"
                        }`}
                      >
                        {locked && <Lock className="w-3 h-3 absolute top-1.5 right-1.5 text-muted-foreground/40" />}
                        {g}
                      </motion.button>
                    );
                  })}
                </div>
                {isPro && selectedGenders.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedGenders([])}
                    className="mt-2 text-[11px] text-muted-foreground hover:underline"
                  >
                    Clear filter (match anyone)
                  </button>
                )}
              </div>

              <motion.button whileTap={{ scale: 0.98 }} onClick={handleApply}
                className="w-full py-3.5 min-h-[48px] rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm">
                Apply
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <ProLockedOverlay isOpen={showProLocked} onClose={() => setShowProLocked(false)} />
      <GuestUpgradePrompt isOpen={showGuestPrompt} onClose={() => setShowGuestPrompt(false)} feature="use premium filters" />
    </>
  );
};

export default FilterPanel;
